import { useEffect } from 'react'
import { Button, Flex, SkeletonText, Td, Text, Tr } from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { Address } from 'viem'
import { useAccount, useBalance, useBlockNumber, useWalletClient } from 'wagmi'

import { formatPriceNumberWithSymbol, formatTokenAmount, getTokenImage, getTokenImageFullPath } from '~/helpers'
import useSupportedNetworkId from '~/hooks/useSupportedNetworkId'
import { useTranslation } from '~/lib/i18n'
import { LensMarket } from '~/types'
import StatText from './StatText'
import TokenImage from './TokenImage'

interface Props {
  market: LensMarket
}

const TokenBalanceRow = ({ market }: Props) => {
  const { t } = useTranslation('faucet')
  const chainId = useSupportedNetworkId()

  const { underlyingSymbol, underlyingToken, underlyingPriceFIAT, underlyingDecimals, isNativeToken } = market

  const { data: walletClient } = useWalletClient()
  const { isConnected, address } = useAccount()
  const { data: blockNumber } = useBlockNumber({ watch: true })
  const { data: balance, queryKey: balanceQueryKey } = useBalance({
    address,
    token: isNativeToken ? undefined : (underlyingToken as Address),
    chainId,
  })

  const queryClient = useQueryClient()
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: balanceQueryKey })
  }, [blockNumber, queryClient, balanceQueryKey])

  const value = Number(balance?.formatted) * underlyingPriceFIAT

  const isLoading = isConnected && isNaN(value)

  const addAsset = () => {
    if (!underlyingToken || !underlyingSymbol || !underlyingDecimals || !walletClient) {
      return
    }

    walletClient.watchAsset({
      type: 'ERC20',
      options: {
        address: underlyingToken,
        symbol: underlyingSymbol,
        decimals: underlyingDecimals,
        image: getTokenImageFullPath(underlyingSymbol),
      },
    })
  }

  const renderColumn = (label: string) => {
    if (!isConnected) {
      return '-'
    }

    if (isLoading) {
      return <SkeletonText noOfLines={1} />
    }

    return <StatText color="text.primary">{label}</StatText>
  }

  return (
    <Tr borderBottom="1px solid #FFFFFF12" _last={{ borderBottom: 'none' }}>
      <Td textTransform="none">
        <Flex alignItems="center" h="30px">
          <TokenImage name={underlyingSymbol} image={getTokenImage(underlyingSymbol)} size="sm" mr={3} />
          <Flex flexDirection="column" fontWeight="bold">
            <Text>{underlyingSymbol}</Text>
            {!isNativeToken && (
              <Button
                pl={0}
                size="xs"
                variant="text"
                color="text.secondary"
                type="button"
                _hover={{ color: 'text.primary' }}
                isDisabled={!isConnected}
                onClick={addAsset}
                fontWeight="regular"
                mt={-1}
              >
                {t('buttons.addAsset')}
              </Button>
            )}
          </Flex>
        </Flex>
      </Td>

      <Td textTransform="none" textAlign="right">
        {renderColumn(formatTokenAmount(Number(balance?.formatted), underlyingSymbol, 2, 'compact'))}
      </Td>

      <Td textTransform="none" textAlign="right" display={{ base: 'none', md: 'table-cell' }}>
        {renderColumn(formatPriceNumberWithSymbol(value))}
      </Td>
    </Tr>
  )
}

export default TokenBalanceRow
